import React, { useState, useEffect, useCallback } from 'react';
import { Search, Star, TrendingUp, TrendingDown } from 'lucide-react';
import { MarketData } from '../hooks/useDatabase';
import { TradingMode } from '../App';
import { TickerData } from '../hooks/useBybitWebSocket';
import { usePrevious } from '../hooks/usePrevious';

interface MarketsProps {
  marketData: MarketData[];
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  tradingMode?: TradingMode;
  tickers?: Record<string, TickerData>;
  coingeckoMarketCapData?: any[];
}

// Predefined list of top 150 crypto pairs - same as in SearchablePairSelector
const TOP_CRYPTO_PAIRS = [
  { symbol: 'BTCUSDT', name: 'BTC/USDT', type: 'crypto', active: true },
  { symbol: 'ETHUSDT', name: 'ETH/USDT', type: 'crypto', active: true },
  { symbol: 'SOLUSDT', name: 'SOL/USDT', type: 'crypto', active: true },
  { symbol: 'XRPUSDT', name: 'XRP/USDT', type: 'crypto', active: true },
  { symbol: 'BNBUSDT', name: 'BNB/USDT', type: 'crypto', active: true },
  { symbol: 'DOGEUSDT', name: 'DOGE/USDT', type: 'crypto', active: true },
  { symbol: 'ADAUSDT', name: 'ADA/USDT', type: 'crypto', active: true },
  { symbol: 'AVAXUSDT', name: 'AVAX/USDT', type: 'crypto', active: true },
  { symbol: 'DOTUSDT', name: 'DOT/USDT', type: 'crypto', active: true },
  { symbol: 'MATICUSDT', name: 'MATIC/USDT', type: 'crypto', active: true },
  { symbol: 'LINKUSDT', name: 'LINK/USDT', type: 'crypto', active: true },
  { symbol: 'LTCUSDT', name: 'LTC/USDT', type: 'crypto', active: true },
  { symbol: 'UNIUSDT', name: 'UNI/USDT', type: 'crypto', active: true },
  { symbol: 'SHIBUSDT', name: 'SHIB/USDT', type: 'crypto', active: true },
  { symbol: 'ATOMUSDT', name: 'ATOM/USDT', type: 'crypto', active: true },
  { symbol: 'ETCUSDT', name: 'ETC/USDT', type: 'crypto', active: true },
  { symbol: 'TRXUSDT', name: 'TRX/USDT', type: 'crypto', active: true },
  { symbol: 'NEARUSDT', name: 'NEAR/USDT', type: 'crypto', active: true },
  { symbol: 'APTUSDT', name: 'APT/USDT', type: 'crypto', active: true },
  { symbol: 'FILUSDT', name: 'FIL/USDT', type: 'crypto', active: true },
  { symbol: 'AAVEUSDT', name: 'AAVE/USDT', type: 'crypto', active: true },
  { symbol: 'ALGOUSDT', name: 'ALGO/USDT', type: 'crypto', active: true },
  { symbol: 'AXSUSDT', name: 'AXS/USDT', type: 'crypto', active: true },
  { symbol: 'BCHUSDT', name: 'BCH/USDT', type: 'crypto', active: true },
  { symbol: 'COMPUSDT', name: 'COMP/USDT', type: 'crypto', active: true },
  { symbol: 'DASHUSDT', name: 'DASH/USDT', type: 'crypto', active: true },
  { symbol: 'EOSUSDT', name: 'EOS/USDT', type: 'crypto', active: true },
  { symbol: 'ICPUSDT', name: 'ICP/USDT', type: 'crypto', active: true },
  { symbol: 'MKRUSDT', name: 'MKR/USDT', type: 'crypto', active: true },
  { symbol: 'NEOUSDT', name: 'NEO/USDT', type: 'crypto', active: true },
  { symbol: 'SNXUSDT', name: 'SNX/USDT', type: 'crypto', active: true },
  { symbol: 'VETUSDT', name: 'VET/USDT', type: 'crypto', active: true },
  { symbol: 'XLMUSDT', name: 'XLM/USDT', type: 'crypto', active: true },
  { symbol: 'XMRUSDT', name: 'XMR/USDT', type: 'crypto', active: true },
  { symbol: 'XTZUSDT', name: 'XTZ/USDT', type: 'crypto', active: true },
  { symbol: 'ZECUSDT', name: 'ZEC/USDT', type: 'crypto', active: true },
  { symbol: 'ZILUSDT', name: 'ZIL/USDT', type: 'crypto', active: true },
  { symbol: '1INCHUSDT', name: '1INCH/USDT', type: 'crypto', active: true },
  { symbol: 'ARUSDT', name: 'AR/USDT', type: 'crypto', active: true },
  { symbol: 'BATUSDT', name: 'BAT/USDT', type: 'crypto', active: true },
  { symbol: 'CRVUSDT', name: 'CRV/USDT', type: 'crypto', active: true },
  { symbol: 'ENJUSDT', name: 'ENJ/USDT', type: 'crypto', active: true },
  { symbol: 'FTMUSDT', name: 'FTM/USDT', type: 'crypto', active: true },
  { symbol: 'GRTUSDT', name: 'GRT/USDT', type: 'crypto', active: true },
  { symbol: 'HBARUSDT', name: 'HBAR/USDT', type: 'crypto', active: true },
  { symbol: 'HOTUSDT', name: 'HOT/USDT', type: 'crypto', active: true },
  { symbol: 'IOTAUSDT', name: 'IOTA/USDT', type: 'crypto', active: true },
  { symbol: 'KAVAUSDT', name: 'KAVA/USDT', type: 'crypto', active: true },
  { symbol: 'KNCUSDT', name: 'KNC/USDT', type: 'crypto', active: true },
  { symbol: 'KSMUSDT', name: 'KSM/USDT', type: 'crypto', active: true },
  { symbol: 'LRCUSDT', name: 'LRC/USDT', type: 'crypto', active: true },
  { symbol: 'MANAUSDT', name: 'MANA/USDT', type: 'crypto', active: true },
  { symbol: 'OMGUSDT', name: 'OMG/USDT', type: 'crypto', active: true },
  { symbol: 'ONTUSDT', name: 'ONT/USDT', type: 'crypto', active: true },
  { symbol: 'QTUMUSDT', name: 'QTUM/USDT', type: 'crypto', active: true },
  { symbol: 'RENUSDT', name: 'REN/USDT', type: 'crypto', active: true },
  { symbol: 'RVNUSDT', name: 'RVN/USDT', type: 'crypto', active: true },
  { symbol: 'SANDUSDT', name: 'SAND/USDT', type: 'crypto', active: true },
  { symbol: 'STORJUSDT', name: 'STORJ/USDT', type: 'crypto', active: true },
  { symbol: 'SUSHIUSDT', name: 'SUSHI/USDT', type: 'crypto', active: true },
  { symbol: 'THETAUSDT', name: 'THETA/USDT', type: 'crypto', active: true },
  { symbol: 'WAVESUSDT', name: 'WAVES/USDT', type: 'crypto', active: true },
  { symbol: 'YFIUSDT', name: 'YFI/USDT', type: 'crypto', active: true },
  { symbol: 'ZRXUSDT', name: 'ZRX/USDT', type: 'crypto', active: true },
  { symbol: 'ANKRUSDT', name: 'ANKR/USDT', type: 'crypto', active: true },
  { symbol: 'BANDUSDT', name: 'BAND/USDT', type: 'crypto', active: true },
  { symbol: 'BELUSDT', name: 'BEL/USDT', type: 'crypto', active: true },
  { symbol: 'BLZUSDT', name: 'BLZ/USDT', type: 'crypto', active: true },
  { symbol: 'CHRUSDT', name: 'CHR/USDT', type: 'crypto', active: true },
  { symbol: 'CHZUSDT', name: 'CHZ/USDT', type: 'crypto', active: true },
  { symbol: 'CKBUSDT', name: 'CKB/USDT', type: 'crypto', active: true },
  { symbol: 'COTIUSDT', name: 'COTI/USDT', type: 'crypto', active: true },
  { symbol: 'CVCUSDT', name: 'CVC/USDT', type: 'crypto', active: true },
  { symbol: 'DENTUSDT', name: 'DENT/USDT', type: 'crypto', active: true },
  { symbol: 'DGBUSDT', name: 'DGB/USDT', type: 'crypto', active: true },
  { symbol: 'DODOUSDT', name: 'DODO/USDT', type: 'crypto', active: true },
  { symbol: 'FETUSDT', name: 'FET/USDT', type: 'crypto', active: true },
  { symbol: 'FLMUSDT', name: 'FLM/USDT', type: 'crypto', active: true },
  { symbol: 'FLOWUSDT', name: 'FLOW/USDT', type: 'crypto', active: true },
  { symbol: 'GALAUSDT', name: 'GALA/USDT', type: 'crypto', active: true },
  { symbol: 'GTCUSDT', name: 'GTC/USDT', type: 'crypto', active: true },
  { symbol: 'ICXUSDT', name: 'ICX/USDT', type: 'crypto', active: true },
  { symbol: 'IOSTUSDT', name: 'IOST/USDT', type: 'crypto', active: true },
  { symbol: 'KLAYUSDT', name: 'KLAY/USDT', type: 'crypto', active: true },
  { symbol: 'MASKUSDT', name: 'MASK/USDT', type: 'crypto', active: true },
  { symbol: 'MTLUSDT', name: 'MTL/USDT', type: 'crypto', active: true },
  { symbol: 'NKNUSDT', name: 'NKN/USDT', type: 'crypto', active: true },
  { symbol: 'OCEANUSDT', name: 'OCEAN/USDT', type: 'crypto', active: true },
  { symbol: 'ONEUSDT', name: 'ONE/USDT', type: 'crypto', active: true },
  { symbol: 'PERPUSDT', name: 'PERP/USDT', type: 'crypto', active: true },
  { symbol: 'REEFUSDT', name: 'REEF/USDT', type: 'crypto', active: true },
  { symbol: 'RSRUSDT', name: 'RSR/USDT', type: 'crypto', active: true },
  { symbol: 'RUNEUSDT', name: 'RUNE/USDT', type: 'crypto', active: true },
  { symbol: 'SKLUSDT', name: 'SKL/USDT', type: 'crypto', active: true },
  { symbol: 'SRMUSDT', name: 'SRM/USDT', type: 'crypto', active: true },
  { symbol: 'STMXUSDT', name: 'STMX/USDT', type: 'crypto', active: true },
  { symbol: 'TOMOUSDT', name: 'TOMO/USDT', type: 'crypto', active: true },
  { symbol: 'TRBUSDT', name: 'TRB/USDT', type: 'crypto', active: true },
  { symbol: 'ZENUSDT', name: 'ZEN/USDT', type: 'crypto', active: true },
  { symbol: 'ALPHAUSDT', name: 'ALPHA/USDT', type: 'crypto', active: true },
  { symbol: 'AUDIOUSDT', name: 'AUDIO/USDT', type: 'crypto', active: true },
  { symbol: 'BAKEUSDT', name: 'BAKE/USDT', type: 'crypto', active: true },
  { symbol: 'BALUSDT', name: 'BAL/USDT', type: 'crypto', active: true },
  { symbol: 'BTCDOMUSDT', name: 'BTCDOM/USDT', type: 'crypto', active: true },
  { symbol: 'C98USDT', name: 'C98/USDT', type: 'crypto', active: true },
  { symbol: 'CELOUSDT', name: 'CELO/USDT', type: 'crypto', active: true },
  { symbol: 'CELRUSDT', name: 'CELR/USDT', type: 'crypto', active: true },
  { symbol: 'CFXUSDT', name: 'CFX/USDT', type: 'crypto', active: true },
  { symbol: 'CTKUSDT', name: 'CTK/USDT', type: 'crypto', active: true },
  { symbol: 'DARUSDT', name: 'DAR/USDT', type: 'crypto', active: true },
  { symbol: 'DUSKUSDT', name: 'DUSK/USDT', type: 'crypto', active: true },
  { symbol: 'ENSUSDT', name: 'ENS/USDT', type: 'crypto', active: true },
  { symbol: 'GALUSDT', name: 'GAL/USDT', type: 'crypto', active: true },
  { symbol: 'GMTUSDT', name: 'GMT/USDT', type: 'crypto', active: true },
  { symbol: 'IMXUSDT', name: 'IMX/USDT', type: 'crypto', active: true },
  { symbol: 'JASMYUSDT', name: 'JASMY/USDT', type: 'crypto', active: true },
  { symbol: 'LPTUSDT', name: 'LPT/USDT', type: 'crypto', active: true },
  { symbol: 'LQTYUSDT', name: 'LQTY/USDT', type: 'crypto', active: true },
  { symbol: 'LUNA2USDT', name: 'LUNA2/USDT', type: 'crypto', active: true },
  { symbol: 'MINAUSDT', name: 'MINA/USDT', type: 'crypto', active: true },
  { symbol: 'OPUSDT', name: 'OP/USDT', type: 'crypto', active: true },
  { symbol: 'PEOPLEUSDT', name: 'PEOPLE/USDT', type: 'crypto', active: true },
  { symbol: 'RNDRUSDT', name: 'RNDR/USDT', type: 'crypto', active: true },
  { symbol: 'STGUSDT', name: 'STG/USDT', type: 'crypto', active: true },
  { symbol: 'SUIUSDT', name: 'SUI/USDT', type: 'crypto', active: true },
  { symbol: 'TUSDT', name: 'T/USDT', type: 'crypto', active: true },
  { symbol: 'WOOUSDT', name: 'WOO/USDT', type: 'crypto', active: true },
  { symbol: 'XEMUSDT', name: 'XEM/USDT', type: 'crypto', active: true }
];

// Predefined list of CFD instruments - ONLY SHOW THESE SPECIFIC INSTRUMENTS
const CFD_INSTRUMENTS = [
  // Forex pairs - Raw format without slashes
  { symbol: 'EURUSD', name: 'EUR/USD', type: 'forex', active: true },
  { symbol: 'GBPUSD', name: 'GBP/USD', type: 'forex', active: true },
  { symbol: 'USDJPY', name: 'USD/JPY', type: 'forex', active: true },
  { symbol: 'USDCHF', name: 'USD/CHF', type: 'forex', active: true },
  { symbol: 'AUDUSD', name: 'AUD/USD', type: 'forex', active: true },
  { symbol: 'USDCAD', name: 'USD/CAD', type: 'forex', active: true },
  { symbol: 'NZDUSD', name: 'NZD/USD', type: 'forex', active: true },
  { symbol: 'EURJPY', name: 'EUR/JPY', type: 'forex', active: true },
  { symbol: 'GBPJPY', name: 'GBP/JPY', type: 'forex', active: true },
  { symbol: 'EURGBP', name: 'EUR/GBP', type: 'forex', active: true },
  
  // Commodities - Raw format without slashes
  { symbol: 'XAUUSD', name: 'Gold/USD', type: 'commodity', active: true },
  { symbol: 'XAGUSD', name: 'Silver/USD', type: 'commodity', active: true },
  { symbol: 'XPTUSD', name: 'Platinum/USD', type: 'commodity', active: true },
  { symbol: 'XPDUSD', name: 'Palladium/USD', type: 'commodity', active: true },
  { symbol: 'WTIUSD', name: 'Oil/USD', type: 'commodity', active: true },
  { symbol: 'NGUSD', name: 'Natural Gas/USD', type: 'commodity', active: true },
  { symbol: 'ALUUSD', name: 'Aluminum/USD', type: 'commodity', active: true },
  { symbol: 'COPPERUSD', name: 'Copper/USD', type: 'commodity', active: true },
  { symbol: 'ZINCUSD', name: 'Zinc/USD', type: 'commodity', active: true },
  
  // Major Stocks
  { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock', active: true },
  { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock', active: true },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock', active: true },
  { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock', active: true },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock', active: true },
  { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock', active: true },
  { symbol: 'NFLX', name: 'Netflix Inc.', type: 'stock', active: true },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock', active: true },
  { symbol: 'BA', name: 'Boeing Co.', type: 'stock', active: true },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock', active: true }
];

const Markets: React.FC<MarketsProps> = ({ 
  marketData, 
  selectedPair, 
  setSelectedPair, 
  currentPrice,
  tradingMode = 'swap',
  tickers = {},
  coingeckoMarketCapData = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all'>('all');
  const [previousPrices, setPreviousPrices] = useState<Record<string, number>>({});

  // Update previous prices when tickers change
  useEffect(() => {
    if (tickers) {
      const newPrices: Record<string, number> = {};
      Object.entries(tickers).forEach(([symbol, tickerData]) => {
        if (tickerData && tickerData.price > 0) {
          newPrices[symbol] = tickerData.price;
        }
      });
      setPreviousPrices(prev => ({...prev, ...newPrices}));
    }
  }, [tickers]);

  const formatPairDisplay = (symbol: string) => {
    // Handle different asset types based on trading mode
    if (tradingMode === 'cfd') {
      // For CFD instruments - display with slashes for UI only
      if (symbol.includes('USD') && !symbol.includes('USDT')) {
        // For forex pairs, format as XXX/YYY
        if (symbol.length === 6) {
          return `${symbol.substring(0, 3)}/${symbol.substring(3, 6)}`;
        }
        
        // For commodities, use their display names
        if (symbol === 'XAUUSD') return 'Gold/USD';
        if (symbol === 'XAGUSD') return 'Silver/USD';
        if (symbol === 'XPTUSD') return 'Platinum/USD';
        if (symbol === 'WTIUSD') return 'Oil/USD';
      }
      // Stock symbols remain as is
      return symbol;
    }
    
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) {
      return `${(volume / 1e9).toFixed(1)}B`;
    } else if (volume >= 1e6) {
      return `${(volume / 1e6).toFixed(1)}M`;
    } else if (volume >= 1e3) {
      return `${(volume / 1e3).toFixed(1)}K`;
    }
    return volume.toFixed(2);
  };

  // Get filtered market data based on predefined lists, search term, and sort by market cap
  const getFilteredMarketData = () => {
    // Use the appropriate predefined list based on trading mode
    const predefinedList = tradingMode === 'cfd' ? CFD_INSTRUMENTS : TOP_CRYPTO_PAIRS;
    
    // Create a map of predefined symbols for quick lookup
    const predefinedSymbols = new Set(predefinedList.map(item => item.symbol));
    
    // Filter market data to only include predefined symbols
    let filtered = marketData.filter(data => {
      // For crypto mode, check against predefined list
      if (tradingMode !== 'cfd') {
        return predefinedSymbols.has(data.symbol) || data.symbol.endsWith('USDT');
      }
      
      // For CFD mode, include only the predefined CFD instruments
      return predefinedSymbols.has(data.symbol);
    });
    
    // Filter out entries with invalid prices (0 or undefined)
    filtered = filtered.filter(data => 
      data.price !== undefined && 
      data.price !== null && 
      !isNaN(data.price) && 
      data.price > 0
    );
    
    // Create a map of market data for quick lookup
    const marketDataMap = new Map(filtered.map(data => [data.symbol, data]));
    
    // If we don't have market data for some predefined symbols, create placeholder entries
    if (tradingMode !== 'cfd') {
      predefinedList.forEach(item => {
        if (!marketDataMap.has(item.symbol)) {
          // Create a placeholder market data entry
          filtered.push({
            symbol: item.symbol,
            price: 0,
            volume_24h: 0,
            change_24h: 0,
            timestamp: new Date().toISOString()
          });
        }
      });
    } else {
      // For CFD mode, ensure we only show the predefined instruments
      filtered = [];
      
      // Add all predefined CFD instruments, with real data if available
      predefinedList.forEach(item => {
        const existingData = marketDataMap.get(item.symbol);
        if (existingData) {
          filtered.push(existingData);
        } else {
          // Create a placeholder with realistic values based on the instrument type
          let placeholderPrice = 0;
          
          if (item.type === 'forex') {
            if (item.symbol === 'EURUSD') placeholderPrice = 1.0850;
            else if (item.symbol === 'GBPUSD') placeholderPrice = 1.2650;
            else if (item.symbol === 'USDJPY') placeholderPrice = 148.50;
            else if (item.symbol === 'USDCHF') placeholderPrice = 0.8950;
            else if (item.symbol === 'AUDUSD') placeholderPrice = 0.6750;
            else if (item.symbol === 'USDCAD') placeholderPrice = 1.3450;
            else if (item.symbol === 'NZDUSD') placeholderPrice = 0.6150;
            else if (item.symbol === 'EURJPY') placeholderPrice = 161.20;
            else if (item.symbol === 'GBPJPY') placeholderPrice = 187.85;
            else if (item.symbol === 'EURGBP') placeholderPrice = 0.8575;
            else placeholderPrice = 1.0000;
          } else if (item.type === 'commodity') {
            if (item.symbol === 'XAUUSD') placeholderPrice = 2324.55;
            else if (item.symbol === 'XAGUSD') placeholderPrice = 29.85;
            else if (item.symbol === 'XPTUSD') placeholderPrice = 985.20;
            else if (item.symbol === 'XPDUSD') placeholderPrice = 1050.75;
            else if (item.symbol === 'WTIUSD') placeholderPrice = 72.30;
            else if (item.symbol === 'NGUSD') placeholderPrice = 2.15;
            else if (item.symbol === 'ALUUSD') placeholderPrice = 2.35;
            else if (item.symbol === 'COPPERUSD') placeholderPrice = 4.25;
            else if (item.symbol === 'ZINCUSD') placeholderPrice = 2.75;
            else placeholderPrice = 100.00;
          } else if (item.type === 'stock') {
            if (item.symbol === 'AAPL') placeholderPrice = 195.50;
            else if (item.symbol === 'TSLA') placeholderPrice = 248.75;
            else if (item.symbol === 'AMZN') placeholderPrice = 185.90;
            else if (item.symbol === 'MSFT') placeholderPrice = 415.25;
            else if (item.symbol === 'NVDA') placeholderPrice = 875.30;
            else if (item.symbol === 'META') placeholderPrice = 485.60;
            else if (item.symbol === 'NFLX') placeholderPrice = 485.20;
            else if (item.symbol === 'GOOGL') placeholderPrice = 175.80;
            else if (item.symbol === 'BA') placeholderPrice = 245.80;
            else if (item.symbol === 'JPM') placeholderPrice = 198.45;
            else placeholderPrice = 100.00;
          }
          
          filtered.push({
            symbol: item.symbol,
            price: placeholderPrice,
            volume_24h: Math.random() * 10000000000,
            change_24h: (Math.random() - 0.5) * 4,
            timestamp: new Date().toISOString()
          });
        }
      });
    }
    
    // Update prices from WebSocket tickers if available
    filtered = filtered.map(item => {
      const ticker = tickers[item.symbol];
      if (ticker && ticker.price > 0) {
        return {
          ...item,
          price: ticker.price,
          change_24h: ticker.priceChangePercent || item.change_24h || 0
        };
      }
      return item;
    });
    
    // Create a market cap map from coingeckoMarketCapData
    const marketCapMap = new Map();
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      coingeckoMarketCapData.forEach(coin => {
        if (coin && coin.symbol) {
          marketCapMap.set(coin.symbol.toUpperCase(), coin.market_cap || 0);
        }
      });
    }
    
    // Sort by market cap (descending) for crypto mode
    if (tradingMode !== 'cfd') {
      filtered.sort((a, b) => {
        // Extract base symbol (remove USDT)
        const aSymbol = a.symbol.replace('USDT', '').toUpperCase();
        const bSymbol = b.symbol.replace('USDT', '').toUpperCase();
        
        // Get market caps from map
        const aMarketCap = marketCapMap.get(aSymbol) || 0;
        const bMarketCap = marketCapMap.get(bSymbol) || 0;
        
        // Sort by market cap (descending)
        return bMarketCap - aMarketCap;
      });
    } else {
      // For CFD mode, sort by type and then by symbol
      // First, create a map to identify the type of each instrument
      const typeMap = new Map();
      CFD_INSTRUMENTS.forEach(item => {
        typeMap.set(item.symbol, item.type);
      });
      
      // Sort by type and then by symbol
      filtered.sort((a, b) => {
        const typeA = typeMap.get(a.symbol) || '';
        const typeB = typeMap.get(b.symbol) || '';
        
        // First sort by type
        if (typeA !== typeB) {
          // Custom order: forex, commodities, stocks
          const typeOrder = { 'forex': 1, 'commodity': 2, 'stock': 3 };
          return typeOrder[typeA] - typeOrder[typeB];
        }
        
        // Then sort by symbol
        return a.symbol.localeCompare(b.symbol);
      });
    }
    
    // Filter by search term if provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(data => 
        data.symbol.toLowerCase().includes(term) ||
        formatPairDisplay(data.symbol).toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredMarketData = getFilteredMarketData();

  return (
    <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700/50 shadow-2xl h-full flex flex-col">
      {/* Markets Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-cyan-400 font-semibold text-lg">
          {tradingMode === 'cfd' ? 'CFD Instruments' : 'Markets'}
        </h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input 
            type="text" 
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-slate-900/50 text-white text-sm pl-10 pr-4 py-2 rounded-xl w-36 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 border border-slate-600/50"
          />
        </div>
      </div>

      {/* Markets List */}
      <div className="flex-1 space-y-2 overflow-y-auto hide-scrollbar">
        {filteredMarketData.map((market, index) => {
          // Get the instrument type for CFD mode
          let instrumentType = 'crypto';
          if (tradingMode === 'cfd') {
            const instrument = CFD_INSTRUMENTS.find(i => i.symbol === market.symbol);
            instrumentType = instrument?.type || 'unknown';
          }
          
          // Add category headers for CFD mode
          const showHeader = tradingMode === 'cfd' && 
            (index === 0 || 
             (index > 0 && 
              CFD_INSTRUMENTS.find(i => i.symbol === market.symbol)?.type !== 
              CFD_INSTRUMENTS.find(i => i.symbol === filteredMarketData[index-1].symbol)?.type));
          
          // Get real-time price from tickers if available
          const ticker = tickers[market.symbol];
          const price = ticker && ticker.price > 0 ? ticker.price : market.price;
          
          // Determine if price has changed for flashing effect
          const prevPrice = previousPrices[market.symbol];
          let priceFlashClass = '';
          if (prevPrice && price !== prevPrice) {
            priceFlashClass = price > prevPrice ? 'animate-flash-green' : 'animate-flash-red';
          }
          
          // Get change percentage from ticker or market data
          const change24h = ticker && ticker.priceChangePercent !== undefined 
            ? ticker.priceChangePercent 
            : market.change_24h || 0;

          return (
            <React.Fragment key={index}>
              {showHeader && (
                <div className="text-xs font-semibold text-slate-400 uppercase mt-4 mb-2 pl-2">
                  {instrumentType === 'forex' ? 'Forex Pairs' : 
                   instrumentType === 'commodity' ? 'Commodities' : 
                   instrumentType === 'stock' ? 'Stocks' : ''}
                </div>
              )}
              <div 
                onClick={() => setSelectedPair(market.symbol)}
                className={`flex items-center justify-between hover:bg-slate-800/50 px-3 py-2 rounded-xl cursor-pointer transition-all duration-300 ${
                  selectedPair === market.symbol ? 'bg-slate-800/50 border-l-2 border-cyan-400 shadow-lg shadow-cyan-400/10' : 'border border-transparent hover:border-slate-600/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Star size={14} className="text-slate-600 hover:text-amber-400 transition-colors" />
                  <span className="text-slate-300 text-sm font-medium">{formatPairDisplay(market.symbol)}</span>
                </div>
                <span className={`text-slate-300 text-sm font-mono ${priceFlashClass}`}>
                  {price > 0 ? price.toFixed(4) : '-'}
                </span>
                <div className="flex items-center gap-2">
                  {change24h >= 0 ? (
                    <TrendingUp size={14} className="text-emerald-400" />
                  ) : (
                    <TrendingDown size={14} className="text-red-400" />
                  )}
                  <span className={`text-sm font-medium ${change24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}%
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {filteredMarketData.length === 0 && (
          <div className="text-center text-slate-500 py-12">
            <p>No markets found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Markets;